<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/icons/CmprIconSolidLock';

import {
  ref, onMounted, provide, computed, useTemplateRef,
} from 'vue';

import { debounce } from 'lodash/function';

import csrfToken from '@cta/shared/csrf';
import AddressStep from './components/AddressStep.vue';
import AmountStep from './components/AmountStep.vue';
import FoundationHintOverlay from './components/FoundationHintOverlay.vue';
import FriendlyCaptcha from '@cta/frontend/components/FriendlyCaptcha.vue';
import PaymentStep from './components/PaymentStep.vue';
import PrefillBankaccountOverlay from './components/PrefillBankaccountOverlay.vue';
import TlsNotice from './components/TlsNotice.vue';
import CtaTicker from '../frontend/components/CtaTicker.vue';
import initializeStore from './utils/store';
import { handle3DS2Redirect } from './utils/adyen3DS';
import scrollIntoView from '@cta/frontend/javascripts/scroll_into_view';
import browserService from '@cta/frontend/services/BrowserService';
import trackingService from '@cta/frontend/services/TrackingService';

const props = defineProps({
  amountChoices: {
    type: Array,
    default() {
      return [];
    },
  },
  amountPrefill: {
    type: String,
    default: undefined,
  },
  salutations: {
    type: Array,
    default() {
      return [];
    },
  },
  defaultSalutation: {
    type: String,
    default: undefined,
  },
  countryCodes: {
    type: Object,
    default() {
      return {};
    },
  },
  defaultCountryCode: {
    type: String,
    default: undefined,
  },
  regions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentTypes: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        salutation: undefined,
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        locality: undefined,
        street: undefined,
        emailaddress: undefined,
        paymentType: undefined,
        owner: undefined,
        iban: undefined,
      };
    },
  },
  validatePath: {
    type: String,
    default: undefined,
  },
  donatePath: {
    type: String,
    default: undefined,
  },
  foundationHintOverlayUnlocked: {
    type: Boolean,
    default: false,
  },
  foundationHintOverlayThreshold: {
    type: Number,
    default: undefined,
  },
  prefillBankaccountOverlayUnlocked: {
    type: Boolean,
    default: false,
  },
  prefillBankaccountAllowPath: {
    type: String,
    default: undefined,
  },
  prefillBankaccountForbidPath: {
    type: String,
    default: undefined,
  },
  prefillBankaccountFormCheckboxEnabled: {
    type: Boolean,
    default: false,
  },
  prefillBankaccountFormCheckboxText: {
    type: String,
    default: '',
  },
  donationAccountIban: {
    type: String,
    default: undefined,
  },
  logoutPath: {
    type: String,
    default: undefined,
  },
  utmParameters: {
    type: Object,
    default: undefined,
  },
  collectAddress: {
    type: Boolean,
    default: false,
  },
  exitUrl: {
    type: String,
    default: undefined,
  },
  prefillBankaccountConsentText: {
    type: String,
    default: '',
  },
  friendlyCaptchaSettings: {
    type: Object,
    default() {
      return {
        showCaptcha: false,
        simulateCaptchaMode: 'off',
        apiEndpoint: undefined,
        sitekey: undefined,
      };
    },
  },
  adyenSettings: {
    type: Object,
    default() {
      return {
        clientKey: undefined,
        environment: undefined,
        gatewayMerchantId: undefined,
        googlePayMerchantId: undefined,
        merchantName: undefined,
        mobilePaymentTypes: {
          type: Array,
          default: () => [],
        },
        paymentPath: undefined,
        paymentMethodsPath: undefined,
      };
    },
  },
  recurringDonationOfferCheckboxLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferEnabled: {
    type: Boolean,
    default: false,
  },
  recurringDonationOfferHintPlacement: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferHintText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonPreselected: {
    type: Boolean,
    default: false,
  },
  recurringDonationOfferRecurringButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferRedirectUrl: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferRequestText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferUiElement: {
    type: String,
    default: undefined,
  },
  tickerData: {
    type: Array,
    default() {
      return [];
    },
  },
});

const store = initializeStore({
  amountPrefill: props.amountPrefill,
  collectAddress: props.collectAddress,
  defaultCountryCode: props.defaultCountryCode,
  defaultSalutation: props.defaultSalutation,
  paymentTypes: props.paymentTypes,
  prefilledAttributes: props.prefilledAttributes,
  recurringDonationOfferOnetimeButtonPreselected: props.recurringDonationOfferOnetimeButtonPreselected,
});
provide('store', store);

const form = useTemplateRef('form');
const amountStep = useTemplateRef('amount-step');
const addressStep = useTemplateRef('address-step');
const paymentStep = useTemplateRef('payment-step');

function openAmountStep() {
  store.openStep = 'amount';
  scrollIntoView(amountStep.value.$el);
}

function openAddressStep() {
  store.openStep = 'address';
  scrollIntoView(addressStep.value.$el);
}

function openPaymentStep() {
  store.openStep = 'payment';
  scrollIntoView(paymentStep.value.$el);
}

function closeAllSteps() {
  store.openStep = undefined;
}

function allStepsClosed() {
  return store.openStep === undefined;
}

function openFirstInvalidStep() {
  if (!store.amountStepValid) {
    openAmountStep();
  } else if (!store.addressStepValid) {
    openAddressStep();
  } else if (!store.paymentStepValid || store.values.paymentType === 'creditcard_adyen') {
    openPaymentStep();
  } else {
    closeAllSteps();
  }
}

async function fetchValidation() {
  const formData = new FormData();
  formData.append('donation[amount]', store.values.amount === undefined ? '' : store.values.amount);
  formData.append('donation[salutation]', store.values.salutation || '');
  formData.append('donation[firstname]', store.values.firstname || '');
  formData.append('donation[lastname]', store.values.lastname || '');
  formData.append('donation[countrycode]', store.values.countrycode || '');
  formData.append('donation[postalcode]', store.values.postalcode || '');
  formData.append('donation[region]', store.values.region || '');
  formData.append('donation[locality]', store.values.locality || '');
  formData.append('donation[street]', store.values.street || '');
  formData.append('donation[emailaddress]', store.values.emailaddress || '');
  formData.append('donation[payment_type]', store.values.paymentType || '');
  formData.append('donation[owner]', store.values.owner || '');
  formData.append('donation[iban]', store.values.rawIban || '');

  const response = await fetch(props.validatePath, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });
  const json = await response.json();

  return { ok: response?.ok, messages: new Map(Object.entries(json)) };
}

async function validate() {
  const { ok, messages } = await fetchValidation();
  if (ok) {
    Object.assign(store.errors, {
      amount: messages.get('amount'),
      firstname: messages.get('firstname'),
      lastname: messages.get('lastname'),
      street: messages.get('street'),
      postalcode: messages.get('postalcode'),
      region: messages.get('region'),
      locality: messages.get('locality'),
      emailaddress: messages.get('emailaddress'),
      paymentType: messages.get('payment_type'),
      owner: messages.get('owner'),
      iban: messages.get('iban'),
    });
  } else {
    window.location.replace(window.location);
  }
}

const throttledValidate = debounce(async () => validate(), 500, { leading: true });

async function isAmountStepValid() {
  await validate();
  return store.amountStepValid;
}

async function isAddressStepValid() {
  await validate();
  return store.addressStepValid;
}

async function isPaymentStepValid() {
  await validate();
  return store.paymentStepValid;
}

const foundationHintModalVisible = ref(false);

function showFoundationHintModal() {
  if (props.foundationHintOverlayUnlocked && store.values.amount >= props.foundationHintOverlayThreshold) {
    foundationHintModalVisible.value = true;
  }
}

const recurringDonationRedirectUrl = computed(() => {
  // After the user gets redirected to the configured sponsor call, the amount she initially wanted to donate
  // (divided by 4) will automatically be preselected.
  return `${props.recurringDonationOfferRedirectUrl}?da=${store.values.amount / 400}`;
});

async function tryOpenAmountStep() {
  if (allStepsClosed()) {
    openAmountStep();
  } else {
    await validate();
    openAmountStep();
  }
}

async function commitAmount(navigate = openFirstInvalidStep) {
  if (await isAmountStepValid()) {
    if (store.values.switchToRecurringDonation) {
      window.location = recurringDonationRedirectUrl.value;
    } else {
      showFoundationHintModal();
      trackingService.dispatchSendForm(amountStep.value.$el, {
        context: 'amount-step',
        value: store.values.amount,
      });

      navigate();
    }
  } else {
    store.amountErrorUnlocked = true;
  }
}

async function tryOpenAddressStep() {
  if (allStepsClosed()) {
    openAddressStep();
  } else if (store.amountStepOpen) {
    await commitAmount(openAddressStep);
  } else {
    if (store.paymentStepOpen) await validate();
    openAddressStep();
  }
}

async function commitAddress(navigate = openFirstInvalidStep) {
  if (await isAddressStepValid()) {
    trackingService.dispatchSendForm(addressStep.value.$el, {
      context: 'address-step',
    });

    navigate();
  } else {
    store.addressErrorUnlocked = true;
  }
}

async function tryOpenPaymentStep() {
  if (allStepsClosed()) {
    openPaymentStep();
  } else if (store.amountStepOpen) {
    await commitAmount(openPaymentStep);
  } else if (store.addressStepOpen) {
    await commitAddress(openPaymentStep);
  } else {
    openPaymentStep();
  }
}

async function commitPayment(navigate = openFirstInvalidStep) {
  if (await isPaymentStepValid()) {
    trackingService.dispatchSendForm(paymentStep.value.$el, {
      context: 'payment-step',
    });

    navigate();
  } else {
    store.paymentErrorUnlocked = true;
  }
}

const prefillBankaccountModalVisible = ref(false);

var submitButtonActive = false;
const submitPending = ref(false);
const captchaError = ref(undefined);
const captchaSolution = ref(!props.friendlyCaptchaSettings.showCaptcha
  || props.friendlyCaptchaSettings.simulateCaptchaMode === 'auto'
  ? 'simulated-response'
  : undefined);
const captchaValid = computed(() => captchaError.value === undefined);
const friendlyCaptchaRef = ref(undefined);

function captchaComplete() {
  captchaError.value = undefined;

  if (!submitButtonActive) {
    trySubmit();
  }
}

async function submit() {
  const formData = new FormData();
  formData.append('donation[amount]', store.values.amount);
  formData.append('donation[salutation]', store.values.salutation || '');
  formData.append('donation[firstname]', store.values.firstname);
  formData.append('donation[lastname]', store.values.lastname);
  formData.append('donation[street]', store.values.street || '');
  formData.append('donation[countrycode]', store.values.countrycode || '');
  formData.append('donation[postalcode]', store.values.postalcode || '');
  formData.append('donation[region]', store.values.region || '');
  formData.append('donation[locality]', store.values.locality || '');
  formData.append('donation[emailaddress]', store.values.emailaddress);
  formData.append('donation[payment_type]', store.values.paymentType);
  formData.append('donation[owner]', store.values.owner || '');
  formData.append('donation[iban]', store.values.rawIban || '');
  formData.append('donation[allow_prefill_bankaccount]', store.values.allowPrefillBankAccount || '');
  formData.append('donation[captcha_solution]', captchaSolution.value || '');
  formData.append('donation[adyen_data]', JSON.stringify(store.values.adyenData) || '');

  Object.entries(props.utmParameters).forEach(([key, value]) => {
    if (value) formData.append(key, value);
  });

  const response = await fetch(props.donatePath, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  if (response.ok) {
    const json = await response.json();
    const messages = new Map(Object.entries(json));
    captchaError.value = messages.get('captcha_solution');

    if (captchaValid.value) {
      if (props.prefillBankaccountOverlayUnlocked && store.values.paymentType === 'directdebit' && !store.values.allowPrefillBankAccount) {
        prefillBankaccountModalVisible.value = true;
      } else if (messages.get('adyen_data') && store.values.paymentType === 'creditcard_adyen') {
        store.errors.adyenData = messages.get('adyen_data');
        store.paymentErrorUnlocked = true;
        friendlyCaptchaRef.value.reset();
        submitPending.value = false;
      } else if (messages.get('3ds2Redirect')) {
        await handle3DS2Redirect(json['3ds2Redirect'], props.adyenSettings);
      } else {
        trackingService.dispatchSendForm(form.value, {
          context: 'donate-form',
          value: store.values.amount,
        });
        browserService.redirectDelayed(json.redirectUrl);
      }
    }
  } else {
    browserService.reload();
  }
}

async function submitClicked() {
  submitPending.value = true;
  submitButtonActive = true;

  if (store.amountStepOpen) await commitAmount();
  if (store.addressStepOpen) await commitAddress();
  if (store.paymentStepOpen) await commitPayment();

  trySubmit();

  submitButtonActive = false;
}

async function trySubmit() {
  if (captchaSolution.value) {
    if (store.amountStepValid && store.addressStepValid && store.paymentStepValid && submitPending.value) {
      await submit();
    }

    setTimeout(() => {
      submitPending.value = false;
    }, 1000);
  }
}

onMounted(async () => {
  if (props.amountPrefill || store.addressPrefilled || store.paymentPrefilled) {
    await validate();

    if (props.amountPrefill && store.amountStepValid) {
      showFoundationHintModal();
      openFirstInvalidStep();
    }

    if (store.addressPrefilled) {
      store.addressErrorUnlocked = !store.addressStepValid;
    }

    if (store.paymentPrefilled) {
      store.paymentErrorUnlocked = !store.paymentStepValid;
    }
  }
});
</script>

<template>
  <form
    ref="form"
    data-analytics-id="donate-call-form"
  >
    <AmountStep
      ref="amount-step"
      :amount-choices="amountChoices"
      :recurring-donation-offer-checkbox-label="recurringDonationOfferCheckboxLabel"
      :recurring-donation-offer-enabled="recurringDonationOfferEnabled"
      :recurring-donation-offer-hint-placement="recurringDonationOfferHintPlacement"
      :recurring-donation-offer-hint-text="recurringDonationOfferHintText"
      :recurring-donation-offer-onetime-button-label="recurringDonationOfferOnetimeButtonLabel"
      :recurring-donation-offer-onetime-button-preselected="recurringDonationOfferOnetimeButtonPreselected"
      :recurring-donation-offer-recurring-button-label="recurringDonationOfferRecurringButtonLabel"
      :recurring-donation-offer-request-text="recurringDonationOfferRequestText"
      :recurring-donation-offer-ui-element="recurringDonationOfferUiElement"
      @commit="commitAmount"
      @try-open="tryOpenAmountStep"
      @validate="throttledValidate"
    />

    <AddressStep
      ref="address-step"
      :collect-address="collectAddress"
      :country-codes="countryCodes"
      :logout-path="logoutPath"
      :prefilled-attributes="prefilledAttributes"
      :regions="regions"
      :salutations="salutations"
      @commit="commitAddress"
      @try-open="tryOpenAddressStep"
      @validate="throttledValidate"
    />

    <PaymentStep
      ref="payment-step"
      :adyen-settings="adyenSettings"
      :donation-account-iban="donationAccountIban"
      :payment-types="paymentTypes"
      :prefill-bankaccount-form-checkbox-show="prefillBankaccountFormCheckboxEnabled && prefillBankaccountOverlayUnlocked"
      :prefill-bankaccount-form-checkbox-text="prefillBankaccountFormCheckboxText"
      :prefilled-attributes="prefilledAttributes"
      @commit="commitPayment"
      @submit="submitClicked"
      @try-open="tryOpenPaymentStep"
      @validate="throttledValidate"
    />

    <FriendlyCaptcha
      ref="friendlyCaptchaRef"
      v-model:solution="captchaSolution"
      :error="captchaError"
      :error-unlocked="!captchaValid"
      :settings="friendlyCaptchaSettings"
      @complete="captchaComplete"
    >
      <CtaTicker :items="tickerData" />
      <cmpr-button
        class="w-100 mb-50"
        data-analytics-context="donate-form"
        data-analytics-id="donate-call-form-submit-button"
        :disabled="submitPending"
        @click.prevent="submitClicked"
      >
        <cmpr-icon-solid-lock />
        Spenden
      </cmpr-button>

      <tls-notice>
        Die Übermittlung Deiner Daten erfolgt sicher via SSL-Verschlüsselung.
      </tls-notice>
    </FriendlyCaptcha>
  </form>

  <FoundationHintOverlay
    v-if="foundationHintOverlayUnlocked"
    :visible="foundationHintModalVisible"
  />

  <PrefillBankaccountOverlay
    v-if="prefillBankaccountOverlayUnlocked"
    :allow-path="prefillBankaccountAllowPath"
    :consent-text="prefillBankaccountConsentText"
    :forbid-path="prefillBankaccountForbidPath"
    :iban="store.values.rawIban"
    :owner="store.values.owner"
    :redirect-url="exitUrl"
    :visible="prefillBankaccountModalVisible"
  />
</template>
